import { useState, useEffect, useRef } from "react";
import {
  Grid,
  Box,
  Card,
  List,
  Stack,
  ListItemButton,
  ListItemText,
  Typography,
  Select,
  MenuItem,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  ToggleButtonGroup,
  ToggleButton,
  InputLabel,
  ButtonGroup,
  Tab,
} from "@mui/material";
import FileUploader from "../common/FileUploader";
import IndefiniteLoader from "../common/IndefiniteLoader";
import {
  addCaps,
  assignCharges,
  convertLigand,
  extractChain,
  finalProtonation,
  fixStructure,
  prepareInit,
  protonateProtein,
  removeLigand,
  splitComplex,
} from "../../views/Lodestar/lodestarApi";
import Viewer from "../common/MolViewer";
import { getProteinChainsAndResidues } from "../../services/protein.service";
import { setGlobalMsgAlerts } from "../GloablMsgAlerts/globalMsgAlertsSlice";
import { isAxiosError } from "axios";
import { useAppDispatch } from "../../app/hooks";
import { errorMessages } from "../../common_variables/ErrorMsgs";
import MissingResiduesPanel from "./MissingResiduesPanel";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import DownloadIcon from "@mui/icons-material/Download";
import CoPilotPopUpAlert from "../MoleculeCopilot/CoPilotPop";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { allModulesDocs } from "../../utils/docList";
import NeedHelp from "../common/NeedHelp";
import LodestarJobs from "./LodestarJobs";

const proteinPrepSteps = [
  { id: "loadPDB", label: "Load PDB", type: "button", status: "initialized" },
  {
    id: "ligandRemoval",
    label: "Remove Ligand",
    type: "button",
    status: "ligand_removed",
  },
  {
    id: "extractChain",
    label: "Extract Chain",
    type: "button",
    status: "chain_extracted",
  },
  {
    id: "structureFixing",
    label: "Fix Structure",
    type: "button",
    status: "structure_fixed",
  },
  {
    id: "capTerminalsAddition",
    label: "Add Cap Terminals",
    type: "button",
    status: "caps_added",
  },
  {
    id: "proteinAndLigandSplit",
    label: "Split Protein and Lignad",
    type: "button",
    status: "complex_split",
  },
  {
    id: "protonate",
    label: "Protonate",
    type: "button",
    status: "protein_protonated",
  },
  {
    id: "convertLigand",
    label: "Convert Ligand",
    type: "button",
    status: "ligand_converted",
  },
  {
    id: "ligandAssignCharge",
    label: "Assign Charges to Ligand",
    type: "button",
    status: "charges_assigned",
  },
  {
    id: "finalProtonation",
    label: "Final Protonation",
    type: "button",
    status: "final_protonation_complete",
  },
];

const moleculeStatus: Record<string, number> = {
  none: -1,
  initialized: 0,
  ligand_removed: 1,
  chain_extracted: 2,
  structure_fixed: 3,
  caps_added: 4,
  complex_split: 5,
  protein_protonated: 6,
  ligand_converted: 7,
  charges_assigned: 8,
  final_protonation_complete: 9,
};

const LodestarInputV2 = () => {
  const [selected, setSelected] = useState("loadPDB");
  const [loading, setLoading] = useState<boolean>(false);
  const [PDBFile, setPDBFile] = useState<File>(null);
  const [jobId, setJobID] = useState<string>("");
  const [ligands, setLigands] = useState<string[]>([]);
  const [selectedLigand, setSelectedLigand] = useState<string>("");
  const [missingResidues, setMissingResidues] = useState<string[]>([]);
  const [molViewer, setMolViewer] = useState<string>("");
  const [selectedChain, setSelectedChain] = useState<string>("");
  const [chains, setChains] = useState<string[]>([]);
  const [pH, setPH] = useState<number>(7);
  const [ligandContent, setLigandContent] = useState<string>("");
  const [currentMoleculeStatus, setCurrentMoleculeStatus] =
    useState<string>("none");
  const [alignment, setAlignment] = useState<string>("protein");
  const [openStartOverDialog, setOpenStartOverDialog] =
    useState<boolean>(false);
  const [openLigRemDialog, setOpenLigRemDialog] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<string>("1");

  const ispHValid = pH >= 0 && pH <= 14;

  let viewer = useRef<Viewer>();

  const dispatch = useAppDispatch();
  const handleFileUpload = async (file: File, name: string) => {
    switch (name) {
      case "pdb":
        setPDBFile(file);
        setTimeout(async () => {
          let text = await file.text();
          updateViewer(text, "pdb");
        }, 100);
        setCurrentMoleculeStatus("none");
        break;
    }
  };

  const downloadFile = (
    content: BlobPart,
    filename: string,
    fileType: string
  ) => {
    const href = URL.createObjectURL(new Blob([content], { type: fileType }));
    const link = Object.assign(document.createElement("a"), {
      href,
      download: filename,
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const handleOutputDownload = () => {
    downloadFile(molViewer, "lodestar_protein_output.pdb", "chemical/x-pdb");
    downloadFile(
      ligandContent,
      "lodestar_ligand_output.sdf",
      "chemical/x-mdl-sdfile"
    );
  };

  const handleSubmitJob = () => {};

  const initViewer = async () => {
    viewer.current = new Viewer();
    try {
      await viewer.current.init("molviewer", {
        layoutShowControls: true,
        layoutShowLeftPanel: false,
        layoutShowSequence: true,
        viewportShowExpand: false,
      });
    } catch (err) {
      // console.log("error", err);
    }
  };

  const updateViewer = async (
    data: string,
    format:
      | "mmcif"
      | "cifCore"
      | "pdb"
      | "pdbqt"
      | "gro"
      | "xyz"
      | "mol"
      | "sdf"
      | "mol2"
  ) => {
    initViewer().then(() => {
      if (!data) return;
      const _ref = viewer?.current?.loadStructureFromData(data, format, {
        dataLabel: "",
      });
    });
  };
  useEffect(() => {
    if (molViewer == null) return;
    setTimeout(() => {
      updateViewer(molViewer, "pdb");
    }, 100);
  }, [molViewer, selected]);

  const handleLigandRemoval = async () => {
    if (ligands.length === 1) {
      setOpenLigRemDialog(true);
      return;
    }
    removeSelectedLigand();
  };

  const removeSelectedLigand = async () => {
    try {
      setLoading(true);
      const response = await removeLigand({
        jobId: jobId,
        ligand: selectedLigand,
      });
      setMolViewer(response.data.pdb_content);
      setLigands(response.data.remaining_ligands);
      setSelectedLigand(response.data?.remaining_ligands[0]);
      setCurrentMoleculeStatus(response.data.status);
    } catch (error) {
      let msg: string = "";
      if (isAxiosError(error)) {
        msg = error.response?.data?.detail?.message;
      }
      dispatch(
        setGlobalMsgAlerts({
          msg: msg || errorMessages.removeLigand,
          isAlert: true,
          color: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChainExtraction = async () => {
    try {
      setLoading(true);
      const response = await extractChain({
        jobId: jobId,
        chain: selectedChain,
      });
      setMolViewer(response.data.pdb_content);
      setCurrentMoleculeStatus(response.data.status);
      // setTriggerViewerUpadate(true);
    } catch (error) {
      let msg: string = "";
      if (isAxiosError(error)) {
        msg = error.response?.data?.detail?.message;
      }
      dispatch(
        setGlobalMsgAlerts({
          msg: msg || errorMessages.extractChain,
          isAlert: true,
          color: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSample = () => {
    const link = document.createElement("a");
    link.download = "lodestar_protein_sample.pdb";
    link.href = "/samples/lodestar/lodestar_protein_sample.pdb";
    link.click();
  };

  const handleNext = async () => {
    setLoading(true);
    try {
      let response;
      let protData;
      switch (selected) {
        case "loadPDB":
          response = await prepareInit(PDBFile);
          setJobID(response.data.job_id);
          setLigands(response.data.ligands);
          setMolViewer(response.data.pdb_content);
          setMissingResidues(response.data.missing_residues);
          setSelectedLigand(response.data.ligands[0]);
          setCurrentMoleculeStatus(response.data.status);
          setSelected("ligandRemoval");
          break;

        case "ligandRemoval":
          setCurrentMoleculeStatus("ligand_removed");
          setSelected("extractChain");
          protData = await getProteinChainsAndResidues(PDBFile);
          setChains(protData.data[0]);
          setSelectedChain(protData.data[0][0]);
          break;

        case "extractChain":
          setCurrentMoleculeStatus("chain_extracted");
          setSelected("structureFixing");
          break;

        case "structureFixing":
          response = await fixStructure(jobId);
          setMolViewer(response.data.pdb_content);
          setMissingResidues(response.data.missing_residues);
          setCurrentMoleculeStatus(response.data.status);
          setSelected("capTerminalsAddition");
          break;

        case "capTerminalsAddition":
          response = await addCaps(jobId);
          setMolViewer(response.data.pdb_content);
          setMissingResidues(response.data.missing_residues);
          setCurrentMoleculeStatus(response.data.status);
          protData = await getProteinChainsAndResidues(PDBFile);
          setLigands(protData.data[1]);
          setSelectedLigand(protData.data[1][0]);
          setSelected("proteinAndLigandSplit");
          break;

        case "proteinAndLigandSplit":
          response = await splitComplex({
            jobId: jobId,
            ligandName: selectedLigand,
          });
          setMolViewer(response.data.protein_content);
          setCurrentMoleculeStatus(response.data.status);
          setLigandContent(response.data.ligand_content);
          setAlignment("protein");
          setSelected("protonate");
          break;

        case "protonate":
          response = await protonateProtein({ jobId: jobId, pH: pH });
          setMolViewer(response.data.protein_content);
          setAlignment("protein");
          setCurrentMoleculeStatus(response.data.status);
          setSelected("convertLigand");
          break;

        case "convertLigand":
          response = await convertLigand({ jobId: jobId, pH: pH });
          setCurrentMoleculeStatus(response.data.status);
          setAlignment("protein");
          setSelected("ligandAssignCharge");
          break;

        case "ligandAssignCharge":
          response = await assignCharges(jobId);
          setCurrentMoleculeStatus(response.data.status);
          setSelected("finalProtonation");
          break;

        case "finalProtonation":
          response = await finalProtonation(jobId);
          setCurrentMoleculeStatus(response.data.status);
          setMolViewer(response.data.protein_content);
          setSelected("finalProtonation");
          break;
      }
    } catch (error) {
      let msg: string = "";
      if (isAxiosError(error)) {
        msg = error.response?.data?.detail;
      }
      dispatch(
        setGlobalMsgAlerts({
          msg: msg || errorMessages.genericError,
          isAlert: true,
          color: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = async () => {
    try {
      setMolViewer("");
      setMissingResidues([]);
      setCurrentMoleculeStatus("none");
      setSelected("loadPDB");
      setPDBFile(null);
    } catch (error) {
      dispatch(
        setGlobalMsgAlerts({
          msg: "Sorry, Unable to preform this action.",
          isAlert: true,
          color: "error",
        })
      );
    }
  };

  const descriptionTab = (
    <>
      <Typography>
        Lodestar is a versatile computational module designed to streamline
        Relative Binding Free Energy (RBFE) calculations, Molecular Dynamics
        (MD) simulations, and comprehensive post-simulation analysis. With
        Lodestar, users can accurately simulate molecular interactions, track
        binding affinities, and gain insightful post-MD analyses, all within a
        seamless workflow tailored for high-throughput computational chemistry
        and drug discovery.
      </Typography>
      <Stack spacing={2} alignItems="flex-end" mt={2}>
        <NeedHelp docUrl={allModulesDocs.lodestar} />
      </Stack>
      <Box
        component="img"
        src="/images/new-descriptive-images/lodestar_descriptive.png"
        alt="Protein Info Visual"
        sx={{
          width: "40%", // Reduces the width to 80% of the parent container
          height: "auto",
          mt: 2, // Adds margin-top for space above the image
          mx: "auto", // Centers the image horizontally
          display: "block", // Ensures the Box behaves like a block element
          p: 2, // Adds padding around the image inside the Box
          pb: 0,
        }}
      />
    </>
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue === "1" && molViewer != "") {
      setTimeout(() => {
        updateViewer(molViewer, "pdb");
        setAlignment("protein");
      }, 70);
    }
    setTabValue(newValue);
  };

  return (
    <Grid container>
      <Grid item sm={12} m={2}>
        <Card>
          <TabContext value={tabValue}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                value={tabValue}
                onChange={handleTabChange}
                aria-label="input tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Input Prep" value="1" />
                <Tab label="Desciption" value="2" />
              </TabList>
            </Box>
            <TabPanel value="1">
              <Grid container>
                <Grid item md={3} display={"flex"} flexDirection={"column"}>
                  <Typography variant="h5" mb={2}>
                    Design Workflow
                  </Typography>
                  <List>
                    {proteinPrepSteps.map((step, index) => (
                      <ListItemButton
                        disableRipple
                        sx={{
                          fontSize: "1.1rem",
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: "transparent",
                            cursor: "default",
                          },
                          mr: 2,
                        }}
                        key={step.id}
                        selected={selected === step.id}
                        disabled={
                          moleculeStatus[step.status] >
                          moleculeStatus[currentMoleculeStatus] + 1
                        }
                      >
                        <ListItemText primary={step.label} />
                      </ListItemButton>
                    ))}
                  </List>
                </Grid>
                <Grid item sm={12} md={9}>
                  <Card
                    sx={{
                      minHeight: "35em",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      border: "1px solid #DEE2E6",
                      boxShadow: "none",
                    }}
                    aria-label="card-container"
                  >
                    {PDBFile && (
                      <>
                        <div
                          id="molviewer"
                          style={{
                            height: "600px",
                            width: "95%",
                            position: "relative",
                            alignSelf: "center",
                            margin: "10px 10px",
                          }}
                        ></div>
                      </>
                    )}
                    {selected === "loadPDB" && (
                      <>
                        <Box
                          sx={{
                            width: "30em",
                            border: "1px solid #DEE2E6",
                            borderRadius: "10px",
                            p: 2,
                          }}
                        >
                          <Stack
                            direction="row"
                            justifyContent={"space-between"}
                            width={"auto"}
                          >
                            <Typography
                              sx={{
                                mt: 1,
                                color: "var(--shade-2900, #29283B)",
                                fontSize: "1rem",
                                fontWeight: "bold",
                              }}
                            >
                              Upload Protein File (.pdb)
                            </Typography>
                            <Button
                              variant="text"
                              onClick={handleDownloadSample}
                            >
                              {"Download Sample"}
                            </Button>
                          </Stack>
                          <FileUploader
                            accept={".pdb"}
                            handleFileUpload={(files) => {
                              handleFileUpload(files[0], "pdb");
                            }}
                            deleteHandlerDisable={() => setPDBFile(null)}
                          />
                        </Box>
                      </>
                    )}

                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"flex-start"}
                      width={"100%"}
                      p={2}
                      overflow={"auto"}
                    >
                      {[
                        "ligandRemoval",
                        "extractChain",
                        "structureFixing",
                      ].includes(selected) && (
                        <MissingResiduesPanel residues={missingResidues} />
                      )}

                      {selected == "ligandRemoval" && (
                        <Box>
                          <Stack direction="row" spacing={2} p={2}>
                            <Select
                              value={selectedLigand}
                              onChange={(e) =>
                                setSelectedLigand(e.target.value)
                              }
                              disabled={ligands.length === 0}
                              sx={{ minWidth: 150 }}
                            >
                              {ligands.map((ligand, idx) => (
                                <MenuItem key={idx} value={ligand}>
                                  {ligand}
                                </MenuItem>
                              ))}
                            </Select>
                            <Button
                              onClick={handleLigandRemoval}
                              variant="outlined"
                              disabled={ligands.length === 0}
                            >
                              Remove Ligand
                            </Button>
                          </Stack>
                        </Box>
                      )}

                      {selected == "extractChain" && (
                        <Box>
                          <Stack direction="row" spacing={2} p={2}>
                            <Select
                              value={selectedChain}
                              label="remove-ligand-list"
                              onChange={(e) => setSelectedChain(e.target.value)}
                              disabled={chains.length === 0}
                              sx={{ minWidth: 150 }}
                            >
                              {chains.map((chain, idx) => (
                                <MenuItem key={idx} value={chain}>
                                  {chain}
                                </MenuItem>
                              ))}
                            </Select>

                            <Button
                              variant="outlined"
                              onClick={handleChainExtraction}
                              disabled={
                                moleculeStatus["chain_extracted"] <=
                                moleculeStatus[currentMoleculeStatus]
                              }
                            >
                              Extract Chain
                            </Button>
                          </Stack>
                        </Box>
                      )}

                      {selected == "proteinAndLigandSplit" && (
                        <Box>
                          <InputLabel id="ligand-select-label">
                            Ligand Name
                          </InputLabel>
                          <Select
                            label="Ligand Name"
                            variant="outlined"
                            sx={{ m: 2 }}
                            value={selectedLigand}
                            onChange={(e) => setSelectedLigand(e.target.value)}
                            disabled={ligands.length === 0}
                          >
                            {ligands.map((ligand, idx) => (
                              <MenuItem key={idx} value={ligand}>
                                {ligand}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      )}

                      {["convertLigand", "protonate"].includes(selected) && (
                        <FormControl fullWidth error={!ispHValid} sx={{ m: 2 }}>
                          <TextField
                            label="Enter pH"
                            // fullWidth
                            type="number"
                            onChange={(e) => setPH(parseInt(e.target.value))}
                            inputProps={{
                              min: 0, // Minimum value
                              max: 14, // Maximum value
                            }}
                            defaultValue={7}
                            value={pH}
                          ></TextField>
                          {!ispHValid && (
                            <FormHelperText>
                              pH value should be between 0 and 14
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}

                      {[
                        "convertLigand",
                        "protonate",
                        "ligandAssignCharge",
                      ].includes(selected) && (
                        <Box m={"auto"}>
                          <ToggleButtonGroup
                            exclusive
                            onChange={(event, newAlignment) => {
                              if (newAlignment !== null) {
                                setAlignment(newAlignment);
                              }
                            }}
                            value={alignment}
                            aria-label="button-group"
                            color="primary"
                          >
                            <ToggleButton
                              value="protein"
                              onClick={() => {
                                setTimeout(() => {
                                  updateViewer(molViewer, "pdb");
                                }, 100);
                              }}
                            >
                              Protein
                            </ToggleButton>
                            <ToggleButton
                              value="ligand"
                              onClick={() => {
                                setTimeout(() => {
                                  updateViewer(ligandContent, "pdb");
                                }, 100);
                              }}
                            >
                              Ligand
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      )}
                    </Stack>
                  </Card>
                  <Stack justifyContent={"space-between"} direction={"row"}>
                    <Button
                      variant={"contained"}
                      size="large"
                      color="error"
                      sx={{
                        mt: 2,
                        "&.Mui-disabled": {
                          color: "red",
                          backgroundColor: "rgba(255,0,0,0.1)",
                        },
                      }}
                      onClick={() => setOpenStartOverDialog(true)}
                      startIcon={<NavigateBeforeIcon />}
                      disabled={selected === "loadPDB"}
                    >
                      Start Over
                    </Button>
                    {moleculeStatus[currentMoleculeStatus] !==
                    moleculeStatus["final_protonation_complete"] ? (
                      <Button
                        variant={"contained"}
                        size="large"
                        sx={{ mt: 2 }}
                        onClick={handleNext}
                        endIcon={<NavigateNextIcon />}
                        disabled={
                          (selected === "loadPDB" && PDBFile === null) ||
                          (["convertLigand", "protonate"].includes(selected) &&
                            !ispHValid)
                        }
                      >
                        Next
                      </Button>
                    ) : (
                      <ButtonGroup
                        variant={"contained"}
                        size="large"
                        sx={{ mt: 2, boxShadow: "none" }}
                      >
                        <Button
                          onClick={handleSubmitJob}
                          endIcon={<RocketLaunchIcon />}
                        >
                          Submit Job
                        </Button>
                        <Button
                          onClick={handleOutputDownload}
                          endIcon={<DownloadIcon />}
                        >
                          Download Output
                        </Button>
                      </ButtonGroup>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value="2">
              <Box>{descriptionTab}</Box>
            </TabPanel>
          </TabContext>

          <CoPilotPopUpAlert
            textPrimary="Do you want to start over ?"
            textAlertDespriction="All progress will be lost"
            textAction="Start Over"
            openDialog={openStartOverDialog}
            setOpenDialog={setOpenStartOverDialog}
            actionId={2}
            onActionCompleted={handleStartOver}
            onActionFailed={() => {}}
          />

          <CoPilotPopUpAlert
            textPrimary="Do you want to remove the last ligand also?"
            textAlertDespriction="You will not be able to revert this change"
            textSecondary={`Selected Ligand : ${selectedLigand}`}
            textAction="Remove Ligand"
            openDialog={openLigRemDialog}
            setOpenDialog={setOpenLigRemDialog}
            actionId={2}
            onActionCompleted={removeSelectedLigand}
            onActionFailed={() => {}}
          />

          <IndefiniteLoader state={loading} />
        </Card>
      </Grid>
        <LodestarJobs />
    </Grid>
  );
};

export default LodestarInputV2;
